import * as EppoSdk from '@eppo/js-client-sdk';

import {
  SubjectAttributes,
  SubjectKeyType,
  VariationType,
  VariationTypes,
  VariationValueForType,
} from './types';

/**
 * Retrieves the assigned variation for a given subject based on the specified flag key and variation type.
 *
 * @param flagKey - The key for the feature flag or experiment, created and found through the Eppo UI.
 * @param subjectAttributes - The attributes of the subject for which the assignment is being retrieved.
 * @param bigPictureContexts - The return value of BigPictureContextBuilder.getContexts() (after BigPictureContextBuilder has been instantiated with any necessary overrides).
 * @param defaultAssignment - The assignment to return if the feature flag is not enabled in the Eppo UI.
 * @param assignmentType - (Optional) The type of the variations subjects will receive. Default is VariationTypes.STRING. See VariationTypes (`STRING`, `NUMERIC`, `INTEGER`, `BOOLEAN`, `JSON`).
 * @returns The assigned variation value for the given subject and flag key. If the assignment cannot be retrieved, the function returns the provided `defaultAssignment`.
 */
export const getAssignment = <T extends VariationType>(
  flagKey: string,
  subjectAttributes: SubjectAttributes,
  bigPictureContexts: Record<string, unknown>,
  defaultAssignment: VariationValueForType<T>,
  assignmentType: T = VariationTypes.STRING as T,
) => {
  try {
    const eppoClient = EppoSdk.getInstance();
    const subjectKey = subjectAttributes.subject_key;
    if (!eppoClient || !subjectKey) {
      return defaultAssignment;
    }

    let subjectAttributesWithBPContexts = {
      bigPictureContexts: JSON.stringify(bigPictureContexts),
      ...subjectAttributes,
    };

    switch (assignmentType) {
      case VariationTypes.STRING:
        return eppoClient.getStringAssignment(
          flagKey,
          subjectKey.toString(),
          subjectAttributesWithBPContexts,
          defaultAssignment as VariationValueForType<typeof VariationTypes.STRING>,
        );
      case VariationTypes.NUMERIC:
        return eppoClient.getNumericAssignment(
          flagKey,
          subjectKey.toString(),
          subjectAttributesWithBPContexts,
          defaultAssignment as VariationValueForType<typeof VariationTypes.NUMERIC>,
        );
      case VariationTypes.INTEGER:
        return eppoClient.getIntegerAssignment(
          flagKey,
          subjectKey.toString(),
          subjectAttributesWithBPContexts,
          defaultAssignment as VariationValueForType<typeof VariationTypes.INTEGER>,
        );
      case VariationTypes.BOOLEAN:
        return eppoClient.getBooleanAssignment(
          flagKey,
          subjectKey.toString(),
          subjectAttributesWithBPContexts,
          defaultAssignment as VariationValueForType<typeof VariationTypes.BOOLEAN>,
        );
      case VariationTypes.JSON:
        return eppoClient.getJSONAssignment(
          flagKey,
          subjectKey.toString(),
          subjectAttributesWithBPContexts,
          defaultAssignment as VariationValueForType<typeof VariationTypes.JSON>,
        );
      default:
        return defaultAssignment;
    }
  } catch (e) {
    return defaultAssignment;
  }
};

/**
 * Builds the subject attributes object used to retrieve and log Eppo assignments.
 *
 * @param subjectKey - The unique identifier for the subject (e.g. 1234).
 * @param subjectKeyType - The type of the subject key (e.g., SubjectKeyTypes.CUID, SubjectKeyTypes.VUID, etc.).
 * @param additionalData - (Optional) A key-value pair object containing any additional attributes needed for targeting (i.e. Audiences and custom rules used within a feature flag).
 * @returns The complete subject attributes object, including the subject_key, subject_key_type, and any additional attributes needed for targeting.
 */
export const buildSubjectAttributes = (
  subjectKey: string | number,
  subjectKeyType: SubjectKeyType,
  additionalData: Record<string, EppoSdk.AttributeType> = {},
): SubjectAttributes => {
  return {
    subject_key: subjectKey,
    subject_key_type: subjectKeyType,
    [subjectKeyType]: subjectKey,
    ...additionalData,
  };
};
